import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { getToken, getUser } from '@/utils/auth';
import { Clock, CreditCard, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Global banner shown to organizations on an active trial.
 * - Polls /api/subscription/status once on mount (billing router has no prefix)
 * - Shows "Trial ends in N days" with a "Convert to Paid" CTA
 * - CTA launches Stripe checkout via /api/stripe/tier/checkout and redirects
 * - HQ/ADMIN only (other roles just see the countdown without the CTA)
 */
export function TrialBanner() {
  const user = getUser();
  const token = getToken();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    if (!user || !token) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setStatus(data);
      } catch {
        // silent
      }
    })();
    return () => { alive = false; };
  }, [user, token]);

  if (!user || !token) return null;
  if (!status?.is_trial) return null;

  const days = Math.max(0, status.days_remaining ?? 0);
  const canConvert = ['HQ', 'ADMIN'].includes(user.role);
  const urgent = days <= 7;
  // Pin the banner (no dismiss button, ignore prior dismissal) when the
  // trial is within 3 days of ending. At that point the user must see
  // the warning on every page — soft-dismissing it is no longer safe.
  const pinned = days <= 3;
  if (dismissed && !pinned) return null;

  const tierId = status.plan;
  const units = status.units || 1;

  const handleConvert = async () => {
    if (!canConvert) return;
    setLaunching(true);
    try {
      const res = await fetch(`${API}/api/stripe/tier/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          selected_tier: tierId,
          units,
          origin_url: window.location.origin
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Unable to start checkout.');
        return;
      }
      const data = await res.json();
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error('Checkout URL missing.');
    } catch {
      toast.error('Checkout request failed.');
    } finally {
      setLaunching(false);
    }
  };

  const tone = urgent
    ? 'bg-rose-50 border-rose-200 text-rose-900'
    : 'bg-indigo-50 border-indigo-200 text-indigo-900';
  const iconTone = urgent ? 'text-rose-600' : 'text-indigo-600';

  return (
    <div
      className={`border-b px-4 py-2.5 flex items-center justify-between gap-3 ${tone}`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Clock className={`w-4 h-4 flex-shrink-0 ${iconTone}`} />
        <p className="text-sm truncate">
          <span className="font-semibold" data-testid="trial-days-remaining">
            {days === 0 ? 'Trial ends today' : `Trial ends in ${days} day${days === 1 ? '' : 's'}`}
          </span>
          <span className="hidden sm:inline ml-1">
            — your {tierId ? tierId.charAt(0).toUpperCase() + tierId.slice(1) : ''} plan will be suspended unless payment is made.
          </span>
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {canConvert && (
          <Button
            size="sm"
            onClick={handleConvert}
            disabled={launching}
            className={`h-7 text-xs ${urgent ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
            data-testid="trial-convert-to-paid-btn"
          >
            {launching
              ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Redirecting…</>
              : <><CreditCard className="w-3 h-3 mr-1" /> Convert to Paid</>
            }
          </Button>
        )}
        {!pinned && (
          <button
            onClick={() => setDismissed(true)}
            className="p-0.5 text-indigo-500 hover:text-indigo-700"
            data-testid="dismiss-trial-banner"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
